import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, MessageSquare, Search, Plus, Send, Clock, CheckCircle,
  AlertTriangle, XCircle, User, RefreshCw, Filter, MoreHorizontal,
  MessageCircle, UserPlus, Tag
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import LanguageSwitcher from '../components/LanguageSwitcher';
import AdminSupportAccess from './AdminSupportAccess';

function AdminSupport({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const navigate = useNavigate();
  
  const [tickets, setTickets] = useState([]);
  const [statusCounts, setStatusCounts] = useState({
    open: 0,
    in_progress: 0,
    waiting_customer: 0,
    resolved: 0,
    closed: 0
  });
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    category: 'all',
    search: ''
  });
  
  const [showTicketDetails, setShowTicketDetails] = useState(false);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newReply, setNewReply] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  
  const [newTicket, setNewTicket] = useState({
    subject: '',
    message: '',
    priority: 'normal',
    category: 'general'
  });

  const PRIORITIES = [
    { value: 'low', label: 'Низкий', color: 'bg-gray-100 text-gray-800' },
    { value: 'normal', label: 'Обычный', color: 'bg-blue-100 text-blue-800' },
    { value: 'high', label: 'Высокий', color: 'bg-orange-100 text-orange-800' },
    { value: 'urgent', label: 'Срочный', color: 'bg-red-100 text-red-800' },
  ];

  const STATUSES = [
    { value: 'open', label: 'Открыт', color: 'bg-green-100 text-green-800', icon: MessageCircle },
    { value: 'in_progress', label: 'В работе', color: 'bg-blue-100 text-blue-800', icon: Clock },
    { value: 'waiting_customer', label: 'Ждёт ответа', color: 'bg-yellow-100 text-yellow-800', icon: User },
    { value: 'resolved', label: 'Решён', color: 'bg-purple-100 text-purple-800', icon: CheckCircle },
    { value: 'closed', label: 'Закрыт', color: 'bg-gray-100 text-gray-800', icon: XCircle },
  ];

  const CATEGORIES = [
    { value: 'general', label: 'Общие вопросы' },
    { value: 'order', label: 'Заказы' },
    { value: 'payment', label: 'Оплата' },
    { value: 'delivery', label: 'Доставка' },
    { value: 'product', label: 'Товары' },
    { value: 'other', label: 'Другое' },
  ];

  useEffect(() => {
    fetchTickets();
  }, [page, filters]);

  const fetchTickets = async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.priority !== 'all' && { priority: filters.priority }),
        ...(filters.category !== 'all' && { category: filters.category }),
        ...(filters.search && { search: filters.search }),
      });
      
      const response = await axios.get(`${API}/support/admin/tickets?${params}`, {
        headers: getAuthHeaders()
      });
      
      setTickets(response.data.tickets || []);
      setStatusCounts(response.data.status_counts || {
        open: 0,
        in_progress: 0,
        waiting_customer: 0,
        resolved: 0,
        closed: 0
      });
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 0);
    } catch (error) {
      toast.error('Не удалось загрузить тикеты');
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const response = await axios.get(`${API}/support/tickets/${ticketId}`, {
        headers: getAuthHeaders()
      });
      setSelectedTicket(response.data);
    } catch (error) {
      toast.error('Не удалось загрузить тикет');
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject || !newTicket.message) {
      toast.error('Заполните все обязательные поля');
      return;
    }
    
    try {
      await axios.post(`${API}/support/tickets`, newTicket, {
        headers: getAuthHeaders()
      });
      toast.success('Тикет создан');
      setShowCreateTicket(false);
      setNewTicket({ subject: '', message: '', priority: 'normal', category: 'general' });
      fetchTickets();
    } catch (error) {
      toast.error('Не удалось создать тикет');
    }
  };

  const handleReply = async () => {
    if (!newReply.trim()) {
      toast.error('Введите сообщение');
      return;
    }
    
    try {
      await axios.post(`${API}/support/tickets/${selectedTicket.id}/reply`, {
        message: newReply,
        is_internal: isInternalNote
      }, { headers: getAuthHeaders() });
      
      toast.success(isInternalNote ? 'Заметка добавлена' : 'Ответ отправлен');
      setNewReply('');
      setIsInternalNote(false);
      fetchTicketDetails(selectedTicket.id);
    } catch (error) {
      toast.error('Не удалось отправить ответ');
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await axios.put(`${API}/support/tickets/${ticketId}/status?status=${newStatus}`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Статус обновлён');
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        fetchTicketDetails(ticketId);
      }
    } catch (error) {
      toast.error('Не удалось обновить статус');
    }
  };

  const handlePriorityChange = async (ticketId, newPriority) => {
    try {
      await axios.put(`${API}/support/tickets/${ticketId}/priority?priority=${newPriority}`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Приоритет обновлён');
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        fetchTicketDetails(ticketId);
      }
    } catch (error) {
      toast.error('Не удалось обновить приоритет');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityConfig = (priority) => {
    return PRIORITIES.find(p => p.value === priority) || PRIORITIES[1];
  };

  const getStatusConfig = (status) => {
    return STATUSES.find(s => s.value === status) || STATUSES[0];
  };

  const getCategoryLabel = (category) => {
    return CATEGORIES.find(c => c.value === category)?.label || category;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <div className="text-lg text-gray-600">Загрузка тикетов...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}/admin`}>
                <Button variant="ghost" size="sm" data-testid="back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Назад
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Поддержка клиентов</h1>
                <p className="text-sm text-gray-500">Управление обращениями и тикетами</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Button
                variant="outline"
                onClick={() => setShowAccess(true)}
                data-testid="support-access-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Доступ в Telegram
              </Button>
              <Button 
                onClick={() => setShowCreateTicket(true)} 
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="create-ticket-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Новый тикет
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 py-6">
        {/* Status Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {STATUSES.map(status => {
            const count = statusCounts[status.value] || 0;
            const StatusIcon = status.icon;
            return (
              <Card 
                key={status.value} 
                className={`cursor-pointer transition-all ${filters.status === status.value ? 'ring-2 ring-emerald-500' : 'hover:shadow-md'}`}
                onClick={() => setFilters(prev => ({ ...prev, status: filters.status === status.value ? 'all' : status.value }))}
              >
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-gray-500">{status.label}</div>
                      <div className="text-2xl font-bold">{count}</div>
                    </div>
                    <StatusIcon className={`w-8 h-8 ${status.color.replace('bg-', 'text-').replace('-100', '-500')}`} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-sm text-gray-600">Поиск</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Номер тикета, тема, клиент..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>

              <div className="w-[150px]">
                <Label className="text-sm text-gray-600">Приоритет</Label>
                <Select 
                  value={filters.priority} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все</SelectItem>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-[180px]">
                <Label className="text-sm text-gray-600">Категория</Label>
                <Select 
                  value={filters.category} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все категории</SelectItem>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({ status: 'all', priority: 'all', category: 'all', search: '' })}
              >
                Сбросить
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tickets List */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="tickets-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Тикет</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Клиент</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Категория</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Приоритет</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Статус</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Создан</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Действия</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {tickets.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                        <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-lg font-medium">Нет тикетов</p>
                        <p className="text-sm">Обращения клиентов появятся здесь</p>
                      </td>
                    </tr>
                  ) : (
                    tickets.map((ticket) => {
                      const priorityConfig = getPriorityConfig(ticket.priority);
                      const statusConfig = getStatusConfig(ticket.status);
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <tr key={ticket.id} className="hover:bg-gray-50" data-testid={`ticket-row-${ticket.id}`}>
                          <td className="px-4 py-3">
                            <div>
                              <span className="font-mono text-sm text-emerald-700 font-medium">
                                {ticket.ticket_number}
                              </span>
                              <p className="font-medium text-gray-900 line-clamp-1">{ticket.subject}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-medium">{ticket.user_name}</div>
                              <div className="text-sm text-gray-500">{ticket.user_email}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant="outline">{getCategoryLabel(ticket.category)}</Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={priorityConfig.color}>{priorityConfig.label}</Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-gray-500">
                            {formatDate(ticket.created_at)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  fetchTicketDetails(ticket.id);
                                  setShowTicketDetails(true);
                                }}
                              >
                                <MessageCircle className="w-4 h-4" />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleStatusChange(ticket.id, 'in_progress')}>
                                    <Clock className="w-4 h-4 mr-2" />
                                    В работу
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleStatusChange(ticket.id, 'waiting_customer')}>
                                    <User className="w-4 h-4 mr-2" />
                                    Ждёт ответа клиента
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleStatusChange(ticket.id, 'resolved')}>
                                    <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                                    Решён
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handlePriorityChange(ticket.id, 'urgent')}>
                                    <AlertTriangle className="w-4 h-4 mr-2 text-red-600" />
                                    Срочный приоритет
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-500">
              Показано {tickets.length} из {total} тикетов
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Назад
              </Button>
              <span className="px-3 py-1 text-sm">
                Страница {page} из {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Далее
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Ticket Details Dialog */}
      <Dialog open={showTicketDetails} onOpenChange={setShowTicketDetails}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="font-mono text-emerald-700">{selectedTicket?.ticket_number}</span>
              <Badge className={getStatusConfig(selectedTicket?.status).color}>
                {getStatusConfig(selectedTicket?.status).label}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedTicket && (
            <div className="space-y-4">
              {/* Ticket Info */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg mb-2">{selectedTicket.subject}</h3>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Клиент:</span>{' '}
                    <span className="font-medium">{selectedTicket.user_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Категория:</span>{' '}
                    <Badge variant="outline">{getCategoryLabel(selectedTicket.category)}</Badge>
                  </div>
                  <div>
                    <span className="text-gray-500">Приоритет:</span>{' '}
                    <Badge className={getPriorityConfig(selectedTicket.priority).color}>
                      {getPriorityConfig(selectedTicket.priority).label}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {selectedTicket.messages?.map((message) => (
                  <div 
                    key={message.id} 
                    className={`p-3 rounded-lg ${
                      message.is_internal 
                        ? 'bg-yellow-50 border border-yellow-200' 
                        : message.sender_type === 'admin' 
                          ? 'bg-emerald-50 border border-emerald-200 ml-8' 
                          : 'bg-white border mr-8'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">
                        {message.sender_name}
                        {message.is_internal && <Badge className="ml-2 bg-yellow-200 text-yellow-800">Внутренняя заметка</Badge>}
                      </span>
                      <span className="text-xs text-gray-500">{formatDate(message.created_at)}</span>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{message.message}</p>
                  </div>
                ))}
              </div>

              {/* Reply Form */}
              {selectedTicket.status !== 'closed' && (
                <div className="border-t pt-4">
                  <div className="space-y-3">
                    <Textarea
                      placeholder="Введите ответ..."
                      value={newReply}
                      onChange={(e) => setNewReply(e.target.value)}
                      rows={3}
                    />
                    <div className="flex items-center justify-between">
                      {user?.role === 'Admin' && (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={isInternalNote}
                            onCheckedChange={setIsInternalNote}
                          />
                          <Label className="text-sm">Внутренняя заметка</Label>
                        </div>
                      )}
                      <Button onClick={handleReply} className="bg-emerald-700 hover:bg-emerald-800">
                        <Send className="w-4 h-4 mr-2" />
                        {isInternalNote ? 'Добавить заметку' : 'Отправить ответ'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              {user?.role === 'Admin' && (
                <div className="border-t pt-4">
                  <Label className="text-sm text-gray-600 mb-2 block">Быстрые действия</Label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange(selectedTicket.id, 'in_progress')}
                    >
                      В работу
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange(selectedTicket.id, 'waiting_customer')}
                    >
                      Ждёт ответа
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange(selectedTicket.id, 'resolved')}
                      className="text-green-600"
                    >
                      Решён
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange(selectedTicket.id, 'closed')}
                      className="text-gray-600"
                    >
                      Закрыть
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Ticket Dialog */}
      <Dialog open={showCreateTicket} onOpenChange={setShowCreateTicket}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Новый тикет</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateTicket} className="space-y-4">
            <div>
              <Label>Тема *</Label>
              <Input
                value={newTicket.subject}
                onChange={(e) => setNewTicket(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Кратко опишите проблему"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Категория</Label>
                <Select
                  value={newTicket.category}
                  onValueChange={(v) => setNewTicket(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Приоритет</Label>
                <Select
                  value={newTicket.priority}
                  onValueChange={(v) => setNewTicket(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Сообщение *</Label>
              <Textarea
                value={newTicket.message}
                onChange={(e) => setNewTicket(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Подробно опишите вашу проблему или вопрос..."
                rows={5}
                required
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateTicket(false)}>
                Отмена
              </Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800">
                Создать тикет
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Telegram Support Mini-App access management */}
      <Dialog open={showAccess} onOpenChange={setShowAccess}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="support-access-dialog">
          <DialogHeader>
            <DialogTitle>Доступ к Telegram мини-приложению поддержки</DialogTitle>
          </DialogHeader>
          <AdminSupportAccess />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminSupport;
